import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import {
  Box,
  Grid,
  Button,
  Checkbox,
  FormGroup,
  Container,
  FormControl,
  CircularProgress,
  FormControlLabel
} from '@material-ui/core';
import { BackNavAdmin, ArticleCard, Search, DialogAllCreateIdea } from '../../components';
import { articleActions, ideaCreateActions, ideaCreateDialogsActions } from '../../actions';
import { BusinessUnitPropsCheckboxList, EmptyState } from '../../components/Shared';
import { portfolioIcon } from '../../assets/imgs';
import { useStyles } from './styles';
import config from '../../config';

export function ArticlesPage() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { ideas, loading, viewEmptyState, classifications } = useSelector(state => state.articlesReducer);
  const { page, sort, limit, filter, hasNextPage } = useSelector(state => state.articlesReducer.paging);
  const { dialogTitleDescription } = useSelector((state) => state.ideaCreateDialogs);
  const { personalizedClassifications  } = useSelector(state => state.domains);
  const { businessUnit } = useSelector(state => state.businessUnitProps);
  const { articleQuestions } = useSelector((state) => state.ideaCreate);
  const [scrollPosition, setScrollPosition] = useState(0);

  const classificationsById = classifications.filter(i => i.checked).map(i => i._id);
  const businessUnitById = businessUnit.filter(i => i.checked && i.value !== 'SELECT_ALL').map(i => i.value);

  useEffect(() => {
    if (classifications, businessUnit) {
      dispatch(articleActions.getIdeasWithInfiniteScroll(
        1,
        sort,
        limit,
        filter,
        classificationsById,
        businessUnitById
      ));
    }
  }, [classifications, businessUnit]);

  useEffect(() => {
    return () => {
      dispatch(articleActions.clearAllArticles());
    }
  }, []);

  useEffect(() => {
    if(personalizedClassifications) {
      dispatch(articleActions.loadClassifications(personalizedClassifications.map((item) => ({
        _id: item._id,
        label: item.name,
        checked: false,
      }))));
    }
  }, []);

  useEffect(() => {
    if (config.SHOW_ADITIONAL_INNOVATION_FIELDS && dialogTitleDescription) {
      dispatch(ideaCreateActions.getArticleQuestions());

      if (articleQuestions) {
        dispatch(ideaCreateActions.loadArticleQuestions(articleQuestions?.map((item) => ({
          _id: item._id,
          question: item.question,
          answer: item?.answer ?? '',
          isSelect: item?.isSelect,
          valuesToSelect: item?.valuesToSelect,
          attachmentRequired: item?.attachmentRequired
        }))));
      }
    }
  }, [articleQuestions, dialogTitleDescription]);

  useEffect(() => {
    if (!loading && scrollPosition > 0) {
      window.scrollTo(0, scrollPosition);
    }
  }, [loading, scrollPosition]);

  function loadMoreIdeas() {
    if (!loading && hasNextPage) {
      const nextPage = (page * 1) + 1;

      setScrollPosition(window.scrollY);

      dispatch(articleActions.getIdeasWithInfiniteScroll(
        nextPage,
        sort,
        limit,
        filter,
        classificationsById,
        businessUnitById
      ));
    }
  }

  function searchIdea(query) {
    dispatch(articleActions.getIdeasWithInfiniteScrollSearch(
      1,
      sort,
      limit,
      { field: 'name', value: query },
      classificationsById,
      businessUnitById
    ));
  }

  function handleOpenDialog(openFunction) {
    dispatch(openFunction())
  }

  const handleChangeClassfication = (event) => {
    const { name, checked } = event.target;
    dispatch(articleActions.changeClassifications({_id: name, checked}));
  };

  return (
    <>
      <DialogAllCreateIdea />
      <BackNavAdmin title={!loading ? t('cases') : t('carregando')} pathname="/">
        <Button
          className={classes.btnNewArticle}
          variant="contained"
          color="primary"
          startIcon={<AddCircleIcon />}
          onClick={() => handleOpenDialog(ideaCreateDialogsActions.openDialogTitleDescription)}
        >
          {t('nova_inovacao')}
        </Button>
      </BackNavAdmin>
      <Container maxWidth="lg" className={classes.root}>
        <Grid container spacing={2}>
          <Grid item xs={12} className={classes.containerHeader}>
            <Grid container spacing={3}>
              <Grid item>
                <FormControl component="fieldset" className={classes.formControl}>
                  <FormGroup className={classes.formGroup}>
                    {classifications.map(item => (
                      <FormControlLabel
                        key={item._id}
                        label={item.label}
                        control={
                          <Checkbox
                            color="primary"
                            name={item._id}
                            checked={item.checked}
                            onChange={handleChangeClassfication}
                            inputProps={{ 'aria-label': item.label }}
                          />
                        }
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <Search
                  query={searchIdea}
                  value={filter.value}
                  placeholder={t('pesquise_pelo_nome_da_ideia')}
                />
              </Grid>
              <Grid item xs={4} md={4}>
                <BusinessUnitPropsCheckboxList localField={false} occupationAreaField={false} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            {loading && (
              <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="60vh">
                <CircularProgress />
              </Box>
            )}

            {!loading && viewEmptyState && (
              <Box marginTop="2rem">
                <EmptyState
                  icon={portfolioIcon}
                  description={t('ainda_nao_ha_inovacoes_publicados')}
                />
              </Box>
            )}
            
            {!loading && !viewEmptyState && (
              <InfiniteScroll
                dataLength={ideas.length}
                next={loadMoreIdeas}
                hasMore={true}
                scrollThreshold={'90%'}
                style={{ overflow: 'hidden', paddingBottom: 20 }}
              >
                <Grid container spacing={2}>
                  {ideas.map((item, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={item._id}>
                      <ArticleCard
                        idea={item}
                        link={`/idea/${item._id}/general`}
                        image={item.cardThumbnail?.url ?? ""}
                        index={index}
                      />
                    </Grid>
                  ))}
                </Grid>
              </InfiniteScroll>
            )}
          </Grid>
        </Grid>
      </Container>
    </>
  );
}